import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  CircularProgress,
  Button,
  Modal,
  Box,
  TextField,
  IconButton,
  Grid,
  Pagination,
  Stack,
  InputAdornment,
  Paper,
  Container,
  Divider,
  Tooltip,
  Alert
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import apiClient from "../../AppClient";

const NestedAccordion = () => {
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [category, setCategory] = useState({
    name: "",
    subCategories: [],
  });
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [deletedSubCategories, setDeletedSubCategories] = useState([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);

  useEffect(() => {
    const fetchCategories = async () => {
      setLoading(true);
      apiClient
        .get("/products/categories/")
        .then((response) => {
          setCategories(response.data);
          setFilteredCategories(response.data);
          setError(null);
        })
        .catch((error) => {
          if (error.response) {
            setError(error.response.data.detail);
            setLoading(false);
          } else {
            setError("حدث خطأ اثناء الاتصال بالسيرفر");
            setLoading(false);
          }
        });
      setLoading(false);
    };

    fetchCategories();
  }, []);

  // Search functionality
  useEffect(() => {
    const filtered = categories.filter(cat =>
      cat.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cat.subcategories?.some(sub =>
        sub.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sub.nodecategories?.some(node =>
          node.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
      )
    );
    setFilteredCategories(filtered);
    setCurrentPage(1);
  }, [searchTerm, categories]);

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCategories = filteredCategories.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredCategories.length / itemsPerPage);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleCategoryNameChange = (e) => {
    setCategory({ ...category, name: e.target.value });
  };

  const handleInputChange = (index, field, value) => {
    const updatedSubCategories = [...category.subCategories];
    updatedSubCategories[index][field] = value;
    setCategory({ ...category, subCategories: updatedSubCategories });
  };

  const handleNodeCategoryChange = (subIndex, nodeIndex, value) => {
    const updatedSubCategories = [...category.subCategories];
    updatedSubCategories[subIndex].nodeCategories[nodeIndex].name = value;
    setCategory({ ...category, subCategories: updatedSubCategories });
  };

  const addSubCategory = () => {
    setCategory({
      ...category,
      subCategories: [
        ...category.subCategories,
        { name: "", nodeCategories: [] },
      ],
    });
  };

  const removeSubCategory = (index) => {
    const subCategory = category.subCategories[index];
    if (subCategory.id) {
      setDeletedSubCategories([...deletedSubCategories, subCategory.id]);
    }
    const updatedSubCategories = category.subCategories.filter(
      (_, i) => i !== index
    );
    setCategory({ ...category, subCategories: updatedSubCategories });
  };

  const addNodeCategory = (subIndex) => {
    const updatedSubCategories = [...category.subCategories];
    updatedSubCategories[subIndex].nodeCategories.push({ name: "" });
    setCategory({ ...category, subCategories: updatedSubCategories });
  };

  const removeNodeCategory = (subIndex, nodeIndex) => {
    const updatedSubCategories = [...category.subCategories];
    updatedSubCategories[subIndex].nodeCategories = updatedSubCategories[
      subIndex
    ].nodeCategories.filter((_, i) => i !== nodeIndex);
    setCategory({ ...category, subCategories: updatedSubCategories });
  };

  const handleSubmit = async () => {
    setLoading(true);
    apiClient
      .post("/products/categories/create/", category)
      .then((response) => {
        alert("تم إنشاء التصنيف بنجاح!");
        setOpenAddModal(false);
        setCategory({
          name: "",
          subCategories: [],
        });
        setError(null);
      })
      .catch((error) => {
        if (error.response) {
          setError(error.response.data.detail);
          setLoading(false);
        } else {
          setError("حدث خطأ اثناء الاتصال بالسيرفر");
          setLoading(false);
        }
      });
    setLoading(false);
    window.location.reload();
  };

  const handleUpdate = async (categoryId) => {
    setEditingCategoryId(categoryId);
    const categoryToUpdate = categories.find((cat) => cat.id === categoryId);

    if (!categoryToUpdate) {
      setCategory({
        name: "",
        subCategories: [],
      });
    } else {
      setCategory({
        name: categoryToUpdate.name || "",
        subCategories: categoryToUpdate.subcategories.map((sub) => ({
          id: sub.id,
          name: sub.name || "",
          nodeCategories: sub.nodecategories.map((node) => ({
            id: node.id,
            name: node.name || "",
          })),
        })),
      });
    }

    setDeletedSubCategories([]);
    setOpenUpdateModal(true);
  };

  const handleUpdateSubmit = async () => {
    const preparedCategory = {
      name: category.name,
      subCategories: category.subCategories.map((sub) => ({
        id: sub.id || undefined,
        name: sub.name,
        nodeCategories: sub.nodeCategories.map((node) => ({
          id: node.id || undefined,
          name: node.name,
        })),
      })),
      deletedSubCategories: deletedSubCategories,
    };

    setLoading(true);
    apiClient
      .put(`products/categories/${editingCategoryId}/update/`, preparedCategory)
      .then((response) => {
        alert("تم تحديث التصنيف بنجاح!");
        setOpenUpdateModal(false);
        setCategory({
          name: "",
          subCategories: [],
        });
        setDeletedSubCategories([]);
        setError(null);
      })
      .catch((error) => {
        if (error.response) {
          setError(error.response.data.detail);
          setLoading(false);
        } else {
          setError("حدث خطأ اثناء الاتصال بالسيرفر");
          setLoading(false);
        }
      });
    setLoading(false);
    window.location.reload();
  };

  const handleDelete = async (categoryId) => {
    const confirmDelete = window.confirm(
      "هل أنت متأكد أنك تريد حذف هذا التصنيف؟"
    );
    if (!confirmDelete) return;

    apiClient
      .delete(`/products/categories/${categoryId}/delete/`)
      .then((response) => {
        setCategories(categories.filter((cat) => cat.id !== categoryId));
      })
      .catch((error) => {
        if (error.response) {
          setError(error.response.data.detail);
          setLoading(false);
        } else {
          setError("حدث خطأ اثناء الاتصال بالسيرفر");
          setLoading(false);
        }
      });
  };

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <div>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button
          variant="contained"
          sx={{ backgroundColor: "darkgreen", "&:hover": { backgroundColor: "#1a3e1a" } }}
          onClick={() => setOpenAddModal(true)}
        >
          اضافة نوع صنف جديد
        </Button>

        <TextField
          variant="outlined"
          size="small"
          placeholder="ابحث عن تصنيف..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: 300 }}
        />
      </Box>

      {currentCategories.map((category) => (
        <Accordion key={category.id}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>{category.name}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {category.subcategories?.map((subcategory) => (
              <Accordion key={subcategory.id}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>{subcategory.name}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {subcategory.nodecategories?.map((node) => (
                    <Typography key={node.id}>- {node.name}</Typography>
                  ))}
                </AccordionDetails>
              </Accordion>
            ))}
          </AccordionDetails>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1, gap: 1 }}>
            <Button
              variant="outlined"
              sx={{
                margin: "5px",
                color: "primary.main",
                borderColor: "primary.main",
                "&:hover": {
                  backgroundColor: "primary.light",
                  borderColor: "primary.main",
                }
              }}
              onClick={() => handleUpdate(category.id)}
            >
              تعديل
            </Button>
            <Button
              variant="outlined"
              sx={{
                margin: "5px",
                color: "error.main",
                borderColor: "error.main",
                "&:hover": {
                  backgroundColor: "error.light",
                  borderColor: "error.main",
                }
              }}
              onClick={() => handleDelete(category.id)}
            >
              حذف
            </Button>
          </Box>
        </Accordion>
      ))}

      {/* Pagination */}
      {filteredCategories.length > itemsPerPage && (
        <Stack spacing={2} sx={{ mt: 2, alignItems: "center" }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            sx={{
              '& .MuiPaginationItem-root': {
                color: 'darkgreen',
              },
              '& .MuiPaginationItem-root.Mui-selected': {
                backgroundColor: 'darkgreen',
                color: 'white',
              },
              '& .MuiPaginationItem-root:hover': {
                backgroundColor: '#e8f5e9',
              },
            }}
            showFirstButton
            showLastButton
          />
        </Stack>
      )}

      {/* Add Category Modal */}
      <Modal open={openAddModal} onClose={() => setOpenAddModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxWidth: 500,
            maxHeight: "90vh",
            overflowY: "auto",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography variant="h6" marginBottom={2} color="darkgreen">
            إضافة تصنيف جديد
          </Typography>
          <TextField
            fullWidth
            label="اسم التصنيف"
            value={category.name}
            onChange={handleCategoryNameChange}
            margin="normal"
          />
          {category.subCategories?.map((subCategory, subIndex) => (
            <Grid container spacing={2} key={subIndex}>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label={`اسم التصنيف الثاني ${subIndex + 1}`}
                  value={subCategory.name}
                  onChange={(e) =>
                    handleInputChange(subIndex, "name", e.target.value)
                  }
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <IconButton onClick={() => removeSubCategory(subIndex)}>
                  <RemoveIcon color="error" />
                </IconButton>
              </Grid>
              {subCategory.nodeCategories?.map((nodeCategory, nodeIndex) => (
                <Grid container spacing={2} key={nodeIndex}>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      label={`اسم تصنيف الثالث ${nodeIndex + 1}`}
                      value={nodeCategory.name}
                      onChange={(e) =>
                        handleNodeCategoryChange(
                          subIndex,
                          nodeIndex,
                          e.target.value
                        )
                      }
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <IconButton
                      onClick={() => removeNodeCategory(subIndex, nodeIndex)}
                    >
                      <RemoveIcon color="error" />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => addNodeCategory(subIndex)}
                  sx={{ color: "darkgreen", borderColor: "darkgreen" }}
                >
                  إضافة تصنيف ثالث
                </Button>
              </Grid>
            </Grid>
          ))}
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addSubCategory}
            sx={{ mt: 2, color: "darkgreen", borderColor: "darkgreen" }}
          >
            إضافة تصنيف ثاني
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{
              mt: 2,
              display: "block",
              backgroundColor: "darkgreen",
              "&:hover": { backgroundColor: "#1a3e1a" }
            }}
          >
            إرسال
          </Button>
        </Box>
      </Modal>

      {/* Update Category Modal */}
      <Modal open={openUpdateModal} onClose={() => setOpenUpdateModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxWidth: 500,
            maxHeight: "90vh",
            overflowY: "auto",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography variant="h6" marginBottom={2} color="darkgreen">
            تعديل التصنيف
          </Typography>
          <TextField
            fullWidth
            label="اسم التصنيف"
            value={category.name}
            onChange={handleCategoryNameChange}
            margin="normal"
          />
          {category.subCategories?.map((subCategory, subIndex) => (
            <Grid container spacing={2} key={subIndex}>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label={`اسم التصنيف الثاني ${subIndex + 1}`}
                  value={subCategory.name}
                  onChange={(e) =>
                    handleInputChange(subIndex, "name", e.target.value)
                  }
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <IconButton onClick={() => removeSubCategory(subIndex)}>
                  <RemoveIcon color="error" />
                </IconButton>
              </Grid>
              {subCategory.nodeCategories?.map((nodeCategory, nodeIndex) => (
                <Grid container spacing={2} key={nodeIndex}>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      label={`اسم تصنيف الثالث ${nodeIndex + 1}`}
                      value={nodeCategory.name}
                      onChange={(e) =>
                        handleNodeCategoryChange(
                          subIndex,
                          nodeIndex,
                          e.target.value
                        )
                      }
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <IconButton
                      onClick={() => removeNodeCategory(subIndex, nodeIndex)}
                    >
                      <RemoveIcon color="error" />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => addNodeCategory(subIndex)}
                  sx={{ color: "darkgreen", borderColor: "darkgreen" }}
                >
                  إضافة تصنيف ثالث
                </Button>
              </Grid>
            </Grid>
          ))}
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addSubCategory}
            sx={{ mt: 2, color: "darkgreen", borderColor: "darkgreen" }}
          >
            إضافة تصنيف ثاني
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateSubmit}
            sx={{
              mt: 2,
              display: "block",
              backgroundColor: "darkgreen",
              "&:hover": { backgroundColor: "#1a3e1a" }
            }}
          >
            تحديث
          </Button>
        </Box>
      </Modal>
    </div>
  );
};

export default NestedAccordion;